import React from "react";
import { Tooltip } from "@material-ui/core";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CloseIcon,
  SectionCard,
  Field,
  InfoOutlinedIcon,
} from "../../ui-kit";
import { DocumentField } from "..";
import Loader from "common/Loader";
import RejectionPanel from "../RejectionPanel";
import { DriverSection, PortalFilterElement, RejectionReason, SectionField } from "../../redux/onBoardingSlice.types";
import { useStyles } from "./DriverVerificationPanel.styles";
import { DOC_STATE, SECTION_STATUS } from "./constants";
import { RejectionPayload } from "./types";
import { useDriverVerificationPanel } from "./useDriverVerificationPanel";
import { ONBOARDING_TEXT } from "../text.constants";

export type { DocumentItem } from "../types";
export type { RejectionPayload } from "./types";

// ── Types ─────────────────────────────────────────────────────────────────

export interface DriverVerificationPanelProps {
  rowData: PortalFilterElement;
  sections: DriverSection[];
  sectionsLoading: boolean;
  onClose: () => void;
  onReject: (payload: RejectionPayload) => void;
  onApprove: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const DriverVerificationPanel = ({
  rowData,
  sections,
  sectionsLoading,
  onClose,
  onReject,
  onApprove,
}: DriverVerificationPanelProps) => {
  const classes = useStyles();

  const {
    scrollAreaRef,
    sectionRefs,
    sortedSections,
    sectionStates,
    sectionPickerRefs,
    docStates,
    sasUrls,
    docLoadingMap,
    makeDocHandlers,
    canApprove,
    canReject,
    handleFooterReject,
    handleSectionReject,
    handleSectionRejectConfirm,
    handleSectionRejectCancel,
    handleSectionSelectReason,
    handleSectionUnreject,
  } = useDriverVerificationPanel({ sections, onReject });

  // ── Field rendering ───────────────────────────────────────────────────

  const renderFields = (fields: SectionField[], rejectionReasons: RejectionReason[]) => {
    const result: JSX.Element[] = [];
    let textBuffer: SectionField[] = [];

    const flushText = (key: string) => {
      if (!textBuffer.length) return;
      result.push(
        <Box key={key} className={classes.fieldsRow}>
          {textBuffer.map((f) => (
            <Field key={f.fieldId} label={f.label ?? ""} value={f.value ?? "-"} />
          ))}
        </Box>
      );
      textBuffer = [];
    };

    fields.forEach((field, idx) => {
      if (!field.type || !field.key) return;
      if (field.type === "text") {
        textBuffer.push(field);
        if (textBuffer.length === 2) flushText(`row-${idx}`);
      } else if (field.type === "image" && field.value) {
        flushText(`before-img-${idx}`);
        result.push(
          <DocumentField
            key={field.fieldId}
            doc={{
              label: field.label || field.fieldId,
              fileName: field.fieldId,
              thumbnailUrl: field.value,
              previewUrls: sasUrls[field.fieldId] ? [sasUrls[field.fieldId]] : [],
            }}
            rejectionReasons={rejectionReasons}
            docState={
              docLoadingMap[field.fieldId]
                ? { ...docStates[field.fieldId], state: DOC_STATE.COLLAPSED }
                : docStates[field.fieldId]
            }
            {...makeDocHandlers(field.fieldId, field.value, rejectionReasons)}
          />
        );
      }
    });
    flushText("tail");
    return result;
  };

  // ── Section rendering ─────────────────────────────────────────────────

  const renderSection = (section: DriverSection) => {
    const sState = sectionStates[section.sectionId];
    const isRejected = sState?.status === SECTION_STATUS.REJECTED;
    const showPicker = sState?.showRejectPicker;
    const rejectionReasons = section.rejectionReasons ?? [];

    const sectionImageFields = section.fields.filter((f) => f.type === "image" && f.key && f.value);
    const rejectDisabled =
      sectionImageFields.length > 0 &&
      !sectionImageFields.every((f) => docStates[f.fieldId]?.viewed === true);

    let headerAction: React.ReactNode = null;
    if (isRejected) {
      headerAction = (
        <Box className={classes.rejectedBanner}>
          <Typography className={classes.rejectedBannerText}>
            {ONBOARDING_TEXT.DVP_REJECTED_PREFIX}
          </Typography>
          <Tooltip title={sState.confirmedReason ?? ""} placement="top" arrow>
            <span style={{ display: "inline-flex", alignItems: "center", color: "inherit", cursor: "pointer", flexShrink: 0 }}>
              <InfoOutlinedIcon style={{ fontSize: 14 }} />
            </span>
          </Tooltip>
          <Button
            size="small"
            className={classes.undoRejectBtn}
            onClick={() => handleSectionUnreject(section.sectionId)}
            disableRipple
          >
            {ONBOARDING_TEXT.DVP_UNDO_BTN}
          </Button>
        </Box>
      );
    } else if (!showPicker) {
      headerAction = (
        <Tooltip
          title={rejectDisabled ? ONBOARDING_TEXT.DVP_REJECT_TOOLTIP : ""}
          arrow
          placement="top"
        >
        <span style={{ cursor: rejectDisabled ? "not-allowed" : "default" }}>
            <Button
              size="small"
              variant="outlined"
              className={classes.rejectSectionBtn}
              disabled={rejectDisabled}
              onClick={() => handleSectionReject(section.sectionId)}
              disableRipple
              style={rejectDisabled ? { pointerEvents: "none" } : undefined}
            >
              {ONBOARDING_TEXT.DVP_REJECT_BTN}
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <div
        key={section.id}
        ref={(el) => { sectionRefs.current[section.sectionId] = el; }}
        data-section-id={section.sectionId}
      >
        <SectionCard title={section.title} subtitle={section.description} headerAction={headerAction}>
          {renderFields(section.fields, rejectionReasons)}

          {showPicker && (
            <div ref={(el) => { sectionPickerRefs.current[section.sectionId] = el; }}>
              <Box className={classes.sectionRejectRow}>
                <RejectionPanel
                  reasons={rejectionReasons}
                  selectedReason={sState?.pendingReason ?? ""}
                  onSelect={(reason) => handleSectionSelectReason(section.sectionId, reason)}
                  onCancel={() => handleSectionRejectCancel(section.sectionId)}
                  onConfirm={() => handleSectionRejectConfirm(section.sectionId)}
                  className={classes.inlinePicker}
                />
              </Box>
            </div>
          )}
        </SectionCard>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box className={classes.panelRoot}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography variant="h6" className={classes.headerTitle}>
            {ONBOARDING_TEXT.DVP_HEADER_TITLE_PREFIX} {rowData.userName}
          </Typography>
          <Chip label={rowData.status} size="small" className={classes.statusBadge} />
        </Box>
        <IconButton className={classes.closeButton} onClick={onClose} aria-label={ONBOARDING_TEXT.DVP_CLOSE_ARIA} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <div ref={scrollAreaRef} className={classes.scrollArea}>
        {sectionsLoading ? <Loader /> : sortedSections.map(renderSection)}
      </div>

      <Box className={classes.footer}>
        <Button
          variant="contained"
          className={classes.footerRejectBtn}
          onClick={handleFooterReject}
          disabled={!canReject}
          disableElevation
        >
          {ONBOARDING_TEXT.DVP_FOOTER_REJECT_BTN}
        </Button>
        <Button
          variant="contained"
          className={classes.footerApproveBtn}
          onClick={() => canApprove && onApprove()}
          disabled={!canApprove}
          disableElevation
        >
          {ONBOARDING_TEXT.DVP_FOOTER_APPROVE_BTN}
        </Button>
      </Box>
    </Box>
  );
};

export default DriverVerificationPanel;

